import React, { useState } from "react";
import { Modal, ModalHeader, ModalContent, ModalFooter, ModalBasicLayout } from "@vibe/core/next";
import { ExistingItemsPolicy, estimateMinutes, formatCount } from "./visualIndicator";

const cardStyle = (on: boolean): React.CSSProperties => ({
  display: "flex",
  gap: 10,
  alignItems: "flex-start",
  padding: 12,
  borderRadius: 6,
  cursor: "pointer",
  ...(on ? { border: "2px solid #0073ea", background: "#f4f9ff" } : { border: "1px solid #c3c6d4", background: "#ffffff" }),
});

const INPUT_STYLE: React.CSSProperties = { margin: "3px 0 0", width: 16, height: 16, accentColor: "#0073ea" };
const BODY_STYLE: React.CSSProperties = { fontSize: 13, color: "#676879", lineHeight: 1.4 };
const LEAD_STYLE: React.CSSProperties = { margin: 0, fontSize: 14, color: "#676879", lineHeight: 1.45 };

const Choice = ({
  title,
  body,
  control,
  selected,
}: {
  title: string;
  body: string;
  control: React.ReactNode;
  selected: boolean;
}) => (
  <label style={cardStyle(selected)}>
    {control}
    <span style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <span style={{ fontSize: 14, fontWeight: 600 }}>{title}</span>
      <span style={BODY_STYLE}>{body}</span>
    </span>
  </label>
);

export type ApplyChoice = "all" | "new";

interface SaveDialogProps {
  /** Displayed indicators, e.g. "✨" and "🔗". */
  from: string;
  to: string;
  estimatedItems: number;
  /** "ask" offers both choices; "always" only confirms. ("never" never opens this dialog.) */
  policy: Exclude<ExistingItemsPolicy, "never">;
  saving?: boolean;
  onConfirm: (choice: ApplyChoice) => void;
  onCancel: () => void;
}

/** Mockup frame 3: Save, when items already show the old indicator. */
export const VisualIndicatorSaveDialog = ({ from, to, estimatedItems, policy, saving, onConfirm, onCancel }: SaveDialogProps) => {
  const [choice, setChoice] = useState<ApplyChoice>("all");
  const count = formatCount(estimatedItems);
  const minutes = estimateMinutes(estimatedItems);
  const renameCopy = `Renames the ${count} existing items in the background, about ${minutes} minutes. Syncing keeps working while it runs.`;

  return (
    <Modal id="visual-indicator-save" show onClose={onCancel} size="medium">
      <ModalBasicLayout>
        <ModalHeader title={`Change the indicator from ${from} to ${to}?`} className="px-8" />
        <ModalContent>
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <p style={LEAD_STYLE}>
              {count} synced items in this account show {from} right now.
            </p>
            {policy === "ask" ? (
              <fieldset style={{ border: 0, margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 8 }}>
                <legend style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>What should change?</legend>
                <Choice
                  title="All items, now and from here on"
                  body={renameCopy}
                  selected={choice === "all"}
                  control={
                    <input type="radio" name="visual-indicator-apply" checked={choice === "all"} onChange={() => setChoice("all")} style={INPUT_STYLE} />
                  }
                />
                <Choice
                  title="Only items synced from now on"
                  body={`Existing items keep ${from}. SIMB still removes either one when an item is unlinked.`}
                  selected={choice === "new"}
                  control={
                    <input type="radio" name="visual-indicator-apply" checked={choice === "new"} onChange={() => setChoice("new")} style={INPUT_STYLE} />
                  }
                />
              </fieldset>
            ) : (
              <p style={LEAD_STYLE}>{renameCopy}</p>
            )}
          </div>
        </ModalContent>
      </ModalBasicLayout>
      <ModalFooter
        primaryButton={{ onClick: () => onConfirm(policy === "ask" ? choice : "all"), text: "Save", loading: saving }}
        secondaryButton={{ onClick: onCancel, text: "Cancel", disabled: saving }}
      />
    </Modal>
  );
};

interface TurnOffDialogProps {
  /** The account's current indicator, displayed ("✨", "[SYNC]"). */
  current: string;
  estimatedItems: number;
  saving?: boolean;
  onConfirm: (alsoRemove: boolean) => void;
  onCancel: () => void;
}

/** Mockup frame 5: switching the indicator off when items already show it. */
export const VisualIndicatorTurnOffDialog = ({ current, estimatedItems, saving, onConfirm, onCancel }: TurnOffDialogProps) => {
  const [remove, setRemove] = useState(true);
  const count = formatCount(estimatedItems);

  return (
    <Modal id="visual-indicator-off" show onClose={onCancel} size="medium">
      <ModalBasicLayout>
        <ModalHeader title="Turn off the visual indicator?" className="px-8" />
        <ModalContent>
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <p style={LEAD_STYLE}>
              New syncs won't get {current}. {count} items already show it.
            </p>
            <Choice
              title={`Also remove ${current} from those ${count} items`}
              body={`Runs in the background, about ${estimateMinutes(estimatedItems)} minutes. Only removes the ${current} SIMB added.`}
              selected={false}
              control={<input type="checkbox" checked={remove} onChange={() => setRemove((r) => !r)} style={INPUT_STYLE} />}
            />
          </div>
        </ModalContent>
      </ModalBasicLayout>
      <ModalFooter
        primaryButton={{ onClick: () => onConfirm(remove), text: "Turn off", loading: saving }}
        secondaryButton={{ onClick: onCancel, text: "Keep it on", disabled: saving }}
      />
    </Modal>
  );
};
